import { useCallback, useEffect, useState } from "react";
import { Clock, Volume1, Volume2 } from "lucide-react";
import { OverallTimer } from "../OverallTimer";

const VOLUME_STORAGE_KEY = "a2duo:audio-volume";
const VOLUME_STEP = 0.125;

function readStoredVolume(): number {
  if (typeof window === "undefined") return 0.5;
  try {
    const raw = window.localStorage.getItem(VOLUME_STORAGE_KEY);
    if (raw === null) return 0.5;
    const v = Number.parseFloat(raw);
    return Number.isFinite(v) ? Math.max(0, Math.min(1, v)) : 0.5;
  } catch {
    return 0.5;
  }
}

function writeStoredVolume(v: number) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(VOLUME_STORAGE_KEY, String(v));
  } catch {
    // private mode / quota — accept loss across refresh.
  }
}

function applyVolumeToAllAudio(v: number) {
  if (typeof document === "undefined") return;
  document.querySelectorAll<HTMLAudioElement>("audio").forEach((a) => {
    a.volume = v;
  });
}

/**
 * DUO chrome header — pixel-faithful clone of the real DUO Spreken exam
 * top bar (`Spreken A2 oefenexamen 1`).
 */
export type DuoHeaderProps = {
  sessionId: string;
  examLabel: string;
  totalMinutes: number;
  active: boolean;
  onExpire?: () => void;
  onWarn?: () => void;
  cogSlot?: React.ReactNode;
};

export function DuoHeader({
  sessionId,
  examLabel,
  totalMinutes,
  active,
  onExpire,
  onWarn,
  cogSlot,
}: DuoHeaderProps) {
  return (
    <header
      data-skin="duo"
      className="grid h-16 w-full grid-cols-[1fr_auto_1fr] items-center bg-[var(--duo-blue)] px-6 text-[var(--duo-blue-text)]"
    >
      <div className="flex items-center gap-4">
        <img
          src="/icons/icon-duo.png"
          alt="A2 Easy"
          width={36}
          height={36}
          loading="lazy"
          className="h-9 w-9"
        />
        <span className="text-base font-medium leading-none">{examLabel}</span>
      </div>
      <DuoClockReveal>
        <OverallTimer
          sessionId={sessionId}
          totalMinutes={totalMinutes}
          active={active}
          onExpire={onExpire}
          onWarnThresholdCrossed={onWarn}
          warnAtRemainingMs={15 * 60 * 1000}
          variant="duo"
        />
      </DuoClockReveal>
      <div className="flex items-center justify-end gap-3">
        <DuoVolumeBars />
        {cogSlot}
      </div>
    </header>
  );
}

function DuoClockReveal({ children }: { children: React.ReactNode }) {
  const [revealed, setRevealed] = useState(false);
  return (
    <div
      className="relative flex items-center justify-center"
      onMouseEnter={() => setRevealed(true)}
      onMouseLeave={() => setRevealed(false)}
    >
      <button
        type="button"
        onClick={() => setRevealed((p) => !p)}
        className="inline-flex items-center gap-2 rounded-sm px-2 py-1 text-[var(--duo-blue-text)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--duo-blue-text)] focus-visible:outline-offset-2"
        aria-label="Toon resterende examentijd"
        aria-expanded={revealed}
      >
        <Clock size={20} strokeWidth={1.8} aria-hidden="true" />
        <span
          className={`overflow-hidden whitespace-nowrap transition-[max-width,opacity] duration-150 ease-out ${
            revealed ? "max-w-[120px] opacity-100" : "max-w-0 opacity-0"
          }`}
          aria-hidden={!revealed}
        >
          {children}
        </span>
      </button>
      {revealed ? (
        <span
          role="tooltip"
          className="pointer-events-none absolute top-full left-1/2 mt-1 -translate-x-1/2 whitespace-nowrap rounded-sm bg-[var(--duo-blue-soft)] px-2 py-1 text-[12px] font-medium text-[var(--duo-blue-text)]"
          style={{ animation: "duo-tooltip-in 150ms ease-out" }}
        >
          Klik voor continue tijdsweergave
        </span>
      ) : null}
    </div>
  );
}

function DuoVolumeBars() {
  const totalBars = 8;
  const [volume, setVolume] = useState<number>(() => readStoredVolume());

  useEffect(() => {
    applyVolumeToAllAudio(volume);
    writeStoredVolume(volume);
    if (typeof document === "undefined") return;
    const observer = new MutationObserver(() => applyVolumeToAllAudio(volume));
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [volume]);

  const setStep = useCallback((delta: number) => {
    setVolume((prev) => {
      const next = Math.max(0, Math.min(1, prev + delta));
      return Math.round(next / VOLUME_STEP) * VOLUME_STEP;
    });
  }, []);

  const litBars = Math.round(volume * totalBars);
  return (
    <div
      role="group"
      aria-label="Volume"
      className="flex items-center gap-1.5 text-[var(--duo-blue-text)]"
    >
      <button
        type="button"
        onClick={() => setStep(-VOLUME_STEP)}
        aria-label="Volume zachter"
        className="inline-flex h-7 w-7 items-center justify-center rounded-sm transition-colors hover:bg-[var(--duo-blue-soft)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--duo-blue-text)] focus-visible:outline-offset-2"
      >
        <Volume1 size={16} strokeWidth={1.8} aria-hidden="true" />
      </button>
      <div
        className="flex items-center gap-[3px]"
        role="meter"
        aria-valuenow={litBars}
        aria-valuemin={0}
        aria-valuemax={totalBars}
      >
        {Array.from({ length: totalBars }, (_, i) => (
          <span
            key={i}
            className="block h-[14px] w-[3px] rounded-[1px] bg-[var(--duo-blue-text)] transition-opacity"
            style={{ opacity: i < litBars ? 0.85 : 0.3 }}
            aria-hidden="true"
          />
        ))}
      </div>
      <button
        type="button"
        onClick={() => setStep(VOLUME_STEP)}
        aria-label="Volume harder"
        className="inline-flex h-7 w-7 items-center justify-center rounded-sm transition-colors hover:bg-[var(--duo-blue-soft)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--duo-blue-text)] focus-visible:outline-offset-2"
      >
        <Volume2 size={16} strokeWidth={1.8} aria-hidden="true" />
      </button>
    </div>
  );
}
